import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  getLeadMagnet,
  postLeadMagnetResponseForm,
  VERIFY,
} from "../api/utils";
import useFetch from "../api/hooks/useFetch";
import cogoToast from "cogo-toast";
import {
  TextField,
  Checkbox,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import axios from "axios";
import "../assets/stylesheets/bootstrap.min.css";
import "../assets/stylesheets/style.css";
import "../assets/stylesheets/responsive.css";

export default function WebinarRegistration() {
  const slug =
    "build-a-business-that-runs-without-you-the-secret-is-in-your-resources";
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    needsGstInvoice: false,
    gstNumber: "",
    companyName: "",
    nicheType: "",
    industry_other: "",
  });
  const [showError, setShowError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [registrationStatus, setRegistrationStatus] = useState("Pending");
  const [paymentLink, setPaymentLink] = useState(null);

  const [isWebinarExpired, setIsWebinarExpired] = useState(false);

  const nicheTypes = [
    "Manufacturing & Industrial",
    "Technology & Software",
    "Professional Services",
    "Retail & E-commerce",
    "Construction & Real Estate",
    "Logistics & Supply Chain",
    "Energy & Utilities",
    "Healthcare & Life Sciences",
    "Education & Nonprofit",
    "Finance & Insurance",
    "Media, Entertainment & Hospitality",
    "Other",
  ];
  const {
    apiData: leadMagnet,
    isLoading: isLeadMagnetLoading,
    error: leadMagnetError,
  } = useFetch(getLeadMagnet, slug);

  useEffect(() => {
    if (leadMagnetError) {
      cogoToast.error("Failed to load content");
    } else if (leadMagnet?.status === "error") {
      cogoToast.error("Failed to load content");
    }

    // Check if webinar date is in the past
    if (leadMagnet?.offering_type === "Webinar" && leadMagnet?.webinar_date) {
      const webinarDate = new Date(leadMagnet.webinar_date);
      const today = new Date();
      if (webinarDate < today) {
        setIsWebinarExpired(true);
      }
    }
  }, [leadMagnetError, leadMagnet]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => {
        resolve(true);
      };
      script.onerror = () => {
        resolve(false);
      };
      document.body.appendChild(script);
    });
  };

  const showRazorpay = async (orderId, responseId) => {
    const res = await loadRazorpayScript();
    if (!res) {
      cogoToast.error(
        "Razorpay SDK failed to load. Please check your connection.",
      );
      return;
    }

    const options = {
      key: "rzp_test_bEeyMzaBIE3iLN",
      amount: leadMagnet.discount_price * 100,
      currency: "INR",
      name: "Plural Code Technologies",
      description: `Registration for workshop: ${leadMagnet?.title}`,
      order_id: orderId,
      handler: async function (response) {
        try {
          // Send payment verification to backend
          const verificationResponse = await axios.post(VERIFY, {
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_order_id: response.razorpay_order_id,
            razorpay_signature: response.razorpay_signature,
            response_id: responseId,
          });

          if (verificationResponse.data.status === "ok") {
            cogoToast.success(
              "Payment successful! You are now registered for this workship.",
            );
            setRegistrationStatus("success");
          } else {
            setRegistrationStatus("failure");
            cogoToast.error(
              "Payment verification failed. Please contact support.",
            );
          }
        } catch (error) {
          setRegistrationStatus("failure");
          console.error("Payment verification error:", error);
          cogoToast.error(
            "Payment verification failed. Please contact support.",
          );
        }
      },
      prefill: {
        name: formData.name,
        email: formData.email,
        contact: formData.phone,
      },
      theme: {
        color: "#3399cc",
      },
      reminder_enable: true,
    };

    const rzp = new window.Razorpay(options);
    rzp.open();
  };

  const submitLeadForm = async () => {
    const {
      name,
      email,
      phone,
      needsGstInvoice,
      gstNumber,
      companyName,
      nicheType,
      industry_other,
    } = formData;

    // Basic validation
    if (!name.trim() || !email.trim() || !phone.trim() || !nicheType) {
      setShowError(true);
      cogoToast.info("Please fill in all required fields");
      return;
    }

    if (nicheType === "Other" && !industry_other.trim()) {
      setShowError(true);
      cogoToast.error("Please specify your niche when selecting 'Other'");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setShowError(true);
      cogoToast.error("Please enter a valid email address");
      return;
    }

    const phoneRegex = /^\d{10}$/;
    if (!phoneRegex.test(phone)) {
      setShowError(true);
      cogoToast.error("Phone number must be exactly 10 digits");
      return;
    }

    // GST validation if needed
    if (needsGstInvoice) {
      if (!gstNumber.trim() || !companyName.trim()) {
        setShowError(true);
        cogoToast.error(
          "Please provide GST number and company name for invoice",
        );
        return;
      }

      const gstRegex =
        /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
      if (!gstRegex.test(gstNumber)) {
        setShowError(true);
        cogoToast.error("Please enter a valid GST number");
        return;
      }
    }

    setIsSubmitting(true);

    try {
      const payload = {
        name,
        email,
        phone,
        slug,
        offering_type: leadMagnet?.offering_type,
        amount: leadMagnet?.discount_price,
        needsGstInvoice,
        niche_type: nicheType,
        industry_other: nicheType === "Other" ? industry_other : null,
        ...(needsGstInvoice && { gstNumber, companyName }),
      };

      const response = await postLeadMagnetResponseForm(payload);

      if (response.status === "ok") {
        if (leadMagnet?.offering_type === "Webinar") {
          // await showRazorpay(response.data.order_id, response.data.id);
          //  setPaymentLink(response.data.payment_link);
          setFormData({
            name: "",
            email: "",
            phone: "",
            needsGstInvoice: false,
            gstNumber: "",
            companyName: "",
            nicheType: "",
            industry_other: "",
          });
          setShowError(false);
          // window.open("https://rzp.io/rzp/hlIBf2T");
          window.location.href = "https://rzp.io/rzp/g7rTfX95";
        }
      } else {
        cogoToast.error(
          response.message || "Something went wrong. Please try again.",
        );
      }
    } catch (err) {
      console.error("Form submission error:", err);
      cogoToast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  //  if (paymentLink) {
  //     return (
  //         <div className="container" style={{ justifyContent: "center", display: "flex" }}>
  //             <div className="top-footer" style={{ paddingVertical: "20 !important", width: "75%" }}>
  //                 <div className="row justify-content-center">
  //                     <div className="col-lg-8 text-center" style={{ paddingTop: 150, paddingBottom: 150 }}>
  //                         <div className="card p-4 p-md-5">
  //                             <h4 className="tc-primary head-font mb-4">Payment Required</h4>
  //                             <div className="icon-wrapper mb-4">
  //                                 <i className="fas fa-external-link-alt" style={{ fontSize: "64px", color: "#3399cc" }}></i>
  //                             </div>
  //                             <p className="mb-4" style={{ color: "#0A2458" }}>
  //                                 Please complete your payment to finalize your registration.
  //                             </p>
  //                             <p className="mb-4" style={{ color: "#0A2458" }}>
  //                                 We've sent the payment link to <strong>{formData.email}</strong>.
  //                             </p>
  //                             <a
  //                                 href={paymentLink}
  //                                 target="_self"
  //                                 rel="noopener noreferrer"
  //                                 className="link_btn mt-4 mx-auto"
  //                             >
  //                                 Complete Payment Now
  //                             </a>
  //                             <p className="mt-3" style={{ color: "#0A2458", fontSize: "0.9rem" }}>
  //                                 If you don't complete payment now, you can use the link we emailed you.
  //                             </p>
  //                         </div>
  //                     </div>
  //                 </div>
  //             </div>
  //         </div>
  //     );
  // }

  if (isLeadMagnetLoading) {
    return (
      <div
        className="container"
        style={{ justifyContent: "center", display: "flex" }}
      >
        <p>Loading...</p>
      </div>
    );
  }

  if (isWebinarExpired) {
    return (
      <div
        className="container"
        style={{ justifyContent: "center", display: "flex" }}
      >
        <div
          className="top-footer"
          style={{
            paddingVertical: "20 !important",
            width: "75%",
          }}
        >
          <div className="row justify-content-center">
            <div
              className="col-lg-8 text-center "
              style={{ paddingTop: 150, paddingBottom: 150 }}
            >
              <div className="card p-4 p-md-5">
                <h1
                  className="tc-primary head-font mb-4"
                  style={{ fontSize: "2.5rem" }}
                >
                  {leadMagnet?.title}
                </h1>
                <div className="icon-wrapper mb-4">
                  <i
                    className="fas fa-exclamation-circle"
                    style={{ fontSize: "64px", color: "#dc3545" }}
                  ></i>
                </div>

                <p className="mb-4" style={{ color: "#0A2458" }}>
                  Registrations for this workshop is now closed.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (registrationStatus === "success") {
    return (
      <div
        className="container"
        style={{ justifyContent: "center", display: "flex" }}
      >
        <div
          className="top-footer"
          style={{
            paddingVertical: "20 !important",
            width: "75%",
          }}
        >
          <div className="row justify-content-center">
            <div
              className="col-lg-8 text-center"
              style={{ paddingTop: 150, paddingBottom: 150 }}
            >
              <div className="card p-4 p-md-5">
                <h4 className="tc-primary head-font mb-4">Thank You!</h4>
                <div className="icon-wrapper mb-4">
                  <i
                    className="fas fa-check-circle"
                    style={{ fontSize: "64px", color: "#28a745" }}
                  ></i>
                </div>
                <p className="fs-24 mb-4" style={{ color: "#495057" }}>
                  Your payment was successful!
                </p>
                <p className="mb-4" style={{ color: "#0A2458" }}>
                  Your registration for workshop{" "}
                  <strong>{leadMagnet?.title}</strong> processed successfully.
                </p>
                <p className="mb-4" style={{ color: "#0A2458" }}>
                  We've sent the workshop details to{" "}
                  <strong>{formData.email}</strong>. Please check your inbox
                  (and spam folder just in case).
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (registrationStatus === "failure") {
    return (
      <div
        className="container"
        style={{ justifyContent: "center", display: "flex" }}
      >
        <div
          className="top-footer"
          style={{
            paddingVertical: "20 !important",
            width: "75%",
          }}
        >
          <div className="row justify-content-center">
            <div
              className="col-lg-8 text-center "
              style={{ paddingTop: 150, paddingBottom: 150 }}
            >
              <div className="card p-4 p-md-5">
                <h4 className="tc-primary head-font mb-4">
                  Registration for workshop: {leadMagnet?.title} failed!
                </h4>
                <div className="icon-wrapper mb-4">
                  <i
                    className="fas fa-exclamation-circle"
                    style={{ fontSize: "64px", color: "#dc3545" }}
                  ></i>
                </div>

                <p className="mb-4" style={{ color: "#0A2458" }}>
                  Payment processing failed. Please contact support.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  // Format date as "12/Jun/2025"
  const formatWebinarDate = (dateString) => {
    if (!dateString) return "Date not specified";

    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const date = new Date(dateString);
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();

    return `${day}th ${month}, ${year}`;
  };

  // Format duration as "2 hr 30 min"
  const formatDuration = (duration) => {
    if (!duration) return "Duration not specified";

    // Assuming duration is in minutes (e.g., "150" minutes)
    const totalMinutes = parseInt(duration);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    let formatted = "";
    if (hours > 0) formatted += `${hours} hr `;
    if (minutes > 0) formatted += `${minutes} min`;

    return formatted || "0 min";
  };
  return (
    <div>
      <main className="wrapper">
        <section className="page-hero-sec">
          <div className="container">
            <div className="row justify-content-center">
              <div className="row justify-content-center">
                <div className="col-lg-6 pb-5">
                  <h1 className="tc-primary head-font text-center pb-1">
                    Workshop Registration
                  </h1>
                  <h4 className="tc-primary head-font text-center pb-5">
                    {leadMagnet?.title}
                  </h4>
                  {/* <p className="tc-primary fs-24" style={{ textAlign: "justify" }}>
                    {leadMagnet?.subtitle}
                  </p> */}

                  <div className="webinar-details mt-4">
                    <p>
                      <strong>Date:</strong>{" "}
                      {formatWebinarDate(leadMagnet?.webinar_date)}
                    </p>
                    <p>
                      <strong>Duration:</strong> {leadMagnet?.duration}
                    </p>
                    <p>
                      <strong>Price:</strong> ₹{leadMagnet?.discount_price}
                    </p>
                  </div>
                </div>
              </div>

              <div className="col-lg-6 col-md-8">
                <div className="row">
                  <div className="col-lg-12">
                    <TextField
                      fullWidth
                      label="Full Name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      error={showError && !formData.name.trim()}
                      helperText={
                        showError && !formData.name.trim()
                          ? "Name is required"
                          : ""
                      }
                      required
                      margin="dense"
                      sx={{
                        "& input": {
                          background: "#e0e5ef",
                        },
                      }}
                      inputProps={{ maxLength: 200 }}
                    />
                  </div>

                  <div className="col-lg-12 mt-3">
                    <TextField
                      fullWidth
                      label="Email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      error={
                        showError &&
                        (!formData.email.trim() ||
                          !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
                      }
                      helperText={
                        showError && !formData.email.trim()
                          ? "Email is required"
                          : showError &&
                              !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
                            ? "Invalid email format"
                            : ""
                      }
                      required
                      margin="dense"
                      sx={{
                        "& input": {
                          background: "#e0e5ef",
                        },
                      }}
                      inputProps={{ maxLength: 200 }}
                    />
                  </div>

                  <div className="col-lg-12 mt-3">
                    <TextField
                      fullWidth
                      label="Phone Number"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      error={
                        showError &&
                        (!formData.phone.trim() ||
                          !/^\d{10}$/.test(formData.phone))
                      }
                      helperText={
                        showError && !formData.phone.trim()
                          ? "Phone is required"
                          : showError && !/^\d{10}$/.test(formData.phone)
                            ? "Must be 10 digits"
                            : ""
                      }
                      required
                      margin="dense"
                      sx={{
                        "& input": {
                          background: "#e0e5ef",
                        },
                      }}
                      inputProps={{ maxLength: 10 }}
                    />
                  </div>

                  <div className="col-lg-12 mt-3">
                    <Select
                      fullWidth
                      name="nicheType"
                      value={formData.nicheType}
                      onChange={handleInputChange}
                      displayEmpty
                      error={showError && !formData.nicheType}
                      sx={{
                        "& .MuiSelect-select": {
                          background: "#e0e5ef",
                          padding: "12px 14px",
                        },
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: "#e0e5ef",
                          },
                          "&:hover fieldset": {
                            borderColor: "#b0b5bf",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#3f51b5",
                          },
                        },
                      }}
                      inputProps={{
                        "aria-label": "Industry Type",
                      }}
                    >
                      <MenuItem value="" disabled>
                        Industry Type
                      </MenuItem>
                      {nicheTypes.map((type) => (
                        <MenuItem
                          key={type}
                          value={type}
                          sx={{ padding: "8px 16px" }}
                        >
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                    {showError && !formData.nicheType && (
                      <p
                        style={{
                          color: "#d32f2f",
                          fontSize: "0.75rem",
                          margin: "3px 14px 0",
                        }}
                      >
                        Industry type is required
                      </p>
                    )}
                  </div>

                  {formData.nicheType === "Other" && (
                    <div className="col-lg-12 mt-3">
                      <TextField
                        fullWidth
                        variant="outlined"
                        label="Specify Your Industry"
                        name="industry_other"
                        value={formData.industry_other}
                        onChange={handleInputChange}
                        error={
                          showError &&
                          formData.nicheType === "Other" &&
                          !formData.industry_other.trim()
                        }
                        helperText={
                          showError &&
                          formData.nicheType === "Other" &&
                          !formData.industry_other.trim()
                            ? "Please specify your industry"
                            : ""
                        }
                        required={formData.nicheType === "Other"}
                        margin="normal"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: "#e0e5ef",
                            },
                            "&:hover fieldset": {
                              borderColor: "#b0b5bf",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#3f51b5",
                            },
                            "& input": {
                              background: "#e0e5ef",
                              padding: "12px 14px",
                            },
                          },
                        }}
                        inputProps={{ maxLength: 100 }}
                      />
                    </div>
                  )}
                  <div className="col-lg-12 mt-3">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.needsGstInvoice}
                          onChange={handleCheckboxChange}
                          name="needsGstInvoice"
                          color="primary"
                        />
                      }
                      label="Do you need a GST invoice for your company?"
                    />
                  </div>

                  {formData.needsGstInvoice && (
                    <>
                      <div className="col-lg-12 mt-3">
                        <TextField
                          fullWidth
                          label="Company Name"
                          name="companyName"
                          value={formData.companyName}
                          onChange={handleInputChange}
                          error={
                            showError &&
                            formData.needsGstInvoice &&
                            !formData.companyName.trim()
                          }
                          helperText={
                            showError &&
                            formData.needsGstInvoice &&
                            !formData.companyName.trim()
                              ? "Company name is required for GST invoice"
                              : ""
                          }
                          required={formData.needsGstInvoice}
                          margin="dense"
                          sx={{
                            "& input": {
                              background: "#e0e5ef",
                            },
                          }}
                          inputProps={{ maxLength: 200 }}
                        />
                      </div>

                      <div className="col-lg-12 mt-3">
                        <TextField
                          fullWidth
                          label="GST Number"
                          name="gstNumber"
                          value={formData.gstNumber}
                          onChange={handleInputChange}
                          error={
                            showError &&
                            formData.needsGstInvoice &&
                            (!formData.gstNumber.trim() ||
                              !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(
                                formData.gstNumber,
                              ))
                          }
                          helperText={
                            showError &&
                            formData.needsGstInvoice &&
                            !formData.gstNumber.trim()
                              ? "GST number is required"
                              : showError &&
                                  formData.needsGstInvoice &&
                                  !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(
                                    formData.gstNumber,
                                  )
                                ? "Invalid GST number format"
                                : ""
                          }
                          required={formData.needsGstInvoice}
                          margin="dense"
                          sx={{
                            "& input": {
                              background: "#e0e5ef",
                            },
                          }}
                          inputProps={{ maxLength: 15 }}
                        />
                      </div>
                    </>
                  )}

                  <div className="col-lg-12 mt-4">
                    <button
                      onClick={submitLeadForm}
                      disabled={isSubmitting}
                      className="link_btn mt-4 mx-auto"
                      style={{ marginBottom: 50 }}
                    >
                      {isSubmitting
                        ? "Processing..."
                        : `Pay ₹${leadMagnet?.discount_price}`}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
